import { useState, useEffect, useCallback, useMemo } from 'react';
import { Dayjs } from 'dayjs';
import { TimeMode } from '@types';
import { SiteMeta } from '@src/types/SiteMeta';
import { TimePeriod, TimeRange } from '@utils/time/types';
import {
  calculateTimeRange,
  getAdjustedTime,
} from '@utils/time/calculateTimeRange';

interface UseTimePeriodFilterParams {
  initialPeriod?: TimePeriod;
  siteMeta: SiteMeta;
  timeMode: TimeMode;
  onTimeRangeChange?: (range: TimeRange) => void;
  resetOnMount?: boolean;
}

interface UseTimePeriodFilterReturn {
  selectedPeriod: TimePeriod;
  timeRange: TimeRange;
  setPeriod: (period: TimePeriod) => void;
  setCustomRange: (from: Dayjs, to: Dayjs) => void;
  resetPeriod: () => void;
  isCustomRange: boolean;
}

export const useTimePeriodFilter = ({
  initialPeriod = '24h',
  siteMeta,
  timeMode,
  onTimeRangeChange,
  resetOnMount = false,
}: UseTimePeriodFilterParams): UseTimePeriodFilterReturn => {
  const [selectedPeriod, setSelectedPeriod] = useState<TimePeriod>(
    resetOnMount ? '24h' : initialPeriod,
  );
  const [customFrom, setCustomFrom] = useState<Dayjs | null>(null);
  const [customTo, setCustomTo] = useState<Dayjs | null>(null);

  const isCustomRange = selectedPeriod === 'custom';

  const timeRange: TimeRange = useMemo(() => {
    if (isCustomRange && customFrom && customTo) {
      return {
        from: getAdjustedTime(customFrom, siteMeta, timeMode),
        to: getAdjustedTime(customTo, siteMeta, timeMode),
      };
    }

    if (isCustomRange) {
      return calculateTimeRange('24h', siteMeta, timeMode);
    }

    return calculateTimeRange(selectedPeriod, siteMeta, timeMode);
  }, [selectedPeriod, customFrom, customTo, siteMeta, timeMode, isCustomRange]);

  useEffect(() => {
    if (onTimeRangeChange) {
      onTimeRangeChange(timeRange);
    }
  }, [timeRange, onTimeRangeChange]);

  const setPeriod = useCallback((period: TimePeriod) => {
    setSelectedPeriod(period);
    if (period !== 'custom') {
      setCustomFrom(null);
      setCustomTo(null);
    }
  }, []);

  const setCustomRange = useCallback((from: Dayjs, to: Dayjs) => {
    setCustomFrom(from);
    setCustomTo(to);
    setSelectedPeriod('custom');
  }, []);

  const resetPeriod = useCallback(() => {
    setSelectedPeriod('24h');
    setCustomFrom(null);
    setCustomTo(null);
  }, []);

  return {
    selectedPeriod,
    timeRange,
    setPeriod,
    setCustomRange,
    resetPeriod,
    isCustomRange,
  };
};
